<template>
  <img
    :src="preparedSrc"
    :alt="alt"
    :loading="lazy ? 'lazy' : 'eager'"
    :data-src="fallbackEnabled ? src : null"
    :data-size-suffix="fallbackEnabled || originalEnabled ? sizeSuffix : null"
    :class="{ 'object-scale-down object-center': preparedSrc === fallbackSrc }"
    :srcset="multipleDpr && !useOriginalSrc ? getSrcSet() : ''"
    @error="setFallback"
  />
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { NO_IMAGE_URL } from "@/core/constants";
import { configInjectionKey } from "@/core/injection-keys";
import { appendSuffixToFilename } from "@/core/utilities";
import { transformUrl, getCdnUrl } from "@/shared/thorlabs/shared/image-resize";

export interface IProps {
  lazy?: boolean;
  src?: string;
  alt?: string;
  fallbackSrc?: string;
  /**
   * First you need to generate thumbnails in admin panel, section "Thumbnails" (vc-module-image-tools).
   * You can also set suffixes there.
   * @see https://github.com/VirtoCommerce/vc-module-image-tools
   */
  sizeSuffix?: "sm" | "md" | "lg";
  multipleDpr?: boolean;
  useOriginalSrc?: boolean;
  defaultSize?: number;
  widthSize?: number | null;
  heightSize?: number | null;
}

const props = withDefaults(defineProps<IProps>(), {
  src: "",
  fallbackSrc: NO_IMAGE_URL,
  defaultSize: 80,
  widthSize: null,
  heightSize: null,
});

const cfg = inject(configInjectionKey);

const fallbackEnabled = ref(false);
const originalEnabled = ref(false);

const preparedSrc = computed<string>(() => {
  if (fallbackEnabled.value || !props.src) {
    return props.fallbackSrc;
  }

  if (props.useOriginalSrc) {
    return props.src;
  }

  const sizeSuffix = props.sizeSuffix ? cfg?.image_thumbnails_suffixes?.[props.sizeSuffix] : "";

  if (originalEnabled.value || !cfg?.image_thumbnails_enabled || !sizeSuffix) {
    return props.multipleDpr ? transformUrl(props.src, getWidth(), getHeight()) : getCdnUrl(props.src);
  }

  if (props.multipleDpr) {
    return transformUrl(props.src, getWidth(), getHeight());
  }

  return appendSuffixToFilename(props.src, `_${sizeSuffix}`);
});

function getSrcSet() {
  if (!props.src) {
    return "";
  }

  const imageRegular = transformUrl(props.src, getWidth(), getHeight());
  const image2x = transformUrl(props.src, getWidth(), getHeight(), 2);
  const image3x = transformUrl(props.src, getWidth(), getHeight(), 3);
  const image4x = transformUrl(props.src, getWidth(), getHeight(), 4);

  if (!imageRegular || !image2x || !image3x || !image4x) {
    return "";
  }

  return `${image4x} 4x, ${image3x} 3x, ${image2x} 2x, ${imageRegular} 1x`;
}

function getWidth() {
  return props.widthSize || props.defaultSize;
}

function getHeight(): number {
  return props.heightSize !== null && props.heightSize !== undefined ? props.heightSize : props.defaultSize;
}

/**
 * Set fallback source when image loading error occurs.
 */
function setFallback(): void {
  if (
    !originalEnabled.value &&
    props.sizeSuffix &&
    cfg?.image_thumbnails_enabled &&
    cfg?.image_thumbnails_original_fallback_enabled
  ) {
    originalEnabled.value = true;
  } else if (!fallbackEnabled.value) {
    fallbackEnabled.value = true;
    originalEnabled.value = false;
  }
}

watch(
  () => props.src + props.sizeSuffix + props.fallbackSrc,
  () => {
    fallbackEnabled.value = false;
    originalEnabled.value = false;
  },
);
</script>
